import { Suspense } from "react";

import { SessionBasedFiinderProvider } from "./components/session-based-fiinder-provider";
import { ChatWidget } from "./components/ui/chat/chat-widget";
import { defaultPersonationContext } from "./lib/personation-context";
import { PdfViewerProvider } from "./components/ui/pdf-viewer/pdf-context";
import { PdfViewer } from "./components/ui/pdf-viewer/pdf-viewer";
import { WidgetOverlayProvider } from "./components/ui/widget/widget-overlay-context";

declare global {
  interface Window {
    FIINDER_WIDGET_VERSION?: string;
    FIINDER_SESSION_SERVER_URL?: string;
    FIINDER_TENANT_ID?: string;
    FIINDER_SESSION_USER_ID?: string;
  }
}

type SessionBasedAppProps = {
  isLeft?: boolean;
  sessionServerUrl?: string;
  tenantId?: string;
  userId?: string;
};

function readWindowValue(
  key: keyof Window,
  fallback?: string
): string | undefined {
  if (typeof window === "undefined") return fallback;
  const value = window[key];
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : fallback;
}

function resolveSessionServerUrl(override?: string): string {
  const value = override ?? readWindowValue("FIINDER_SESSION_SERVER_URL");
  if (!value || value.length === 0) {
    throw new Error(
      "SessionBasedApp requires FIINDER_SESSION_SERVER_URL to be provided via props or window.FIINDER_SESSION_SERVER_URL"
    );
  }
  return value;
}

function resolveTenantId(override?: string): string {
  const value = override ?? readWindowValue("FIINDER_TENANT_ID");
  if (!value || value.length === 0) {
    throw new Error(
      "SessionBasedApp requires FIINDER_TENANT_ID to be provided via props or window.FIINDER_TENANT_ID"
    );
  }
  return value;
}

function resolveUserId(override?: string): string {
  const trimmedOverride = override?.trim();
  const value = trimmedOverride ?? readWindowValue("FIINDER_SESSION_USER_ID");
  if (!value || value.length === 0) {
    throw new Error(
      "SessionBasedApp requires FIINDER_SESSION_USER_ID to be provided via props or window.FIINDER_SESSION_USER_ID"
    );
  }
  return value;
}

export function SessionBasedApp(props: SessionBasedAppProps) {
  const sessionServerUrl = resolveSessionServerUrl(props.sessionServerUrl);
  const tenantId = resolveTenantId(props.tenantId);
  const userId = resolveUserId(props.userId);

  return (
    <Suspense fallback={<div>Loading app...</div>}>
      <SessionBasedFiinderProvider
        sessionServerUrl={sessionServerUrl}
        tenantId={tenantId}
        userId={userId}
        fiinderApiUrl={defaultPersonationContext.apiUrl}
        agentName={defaultPersonationContext.botName}
        agentRole={defaultPersonationContext.botRole}
        agentAvatarUrl={defaultPersonationContext.botAvatarUrl}
        companyMainColor={defaultPersonationContext.companyMainColor}
        introMessage={defaultPersonationContext.introMessage}
        introMessageMeta={defaultPersonationContext.introMessageMeta}
        promptSuggestionsTitle={
          defaultPersonationContext.promptSuggestionsTitle
        }
        promptSuggestions={defaultPersonationContext.promptSuggestions}
        launcherCallToAction={defaultPersonationContext.launcherCallToAction}
        launcherAvatarSize={defaultPersonationContext.launcherAvatarSize}
        linkTarget={defaultPersonationContext.linkTarget}
      >
        <WidgetOverlayProvider>
          <PdfViewerProvider>
            <ChatWidget isLeft={props.isLeft} />
            <PdfViewer />
          </PdfViewerProvider>
        </WidgetOverlayProvider>
      </SessionBasedFiinderProvider>
    </Suspense>
  );
}

export default SessionBasedApp;
